import React, { useMemo, useState } from "react";
import classNames from "classnames";

import Button from "components/Button";
import RunConfirmationModal from "../RunConfirmationModal";

import {
    buildCommunityReadinessChecks,
    COMMUNITY_RUN_DISABLED_TONE,
    getReadinessSummary,
} from "utils/communities/communityReadiness";

import classes from "./styles.module.scss";

const RUN_DISABLED_TONE_CLASS = {
    [COMMUNITY_RUN_DISABLED_TONE.INFO]: classes.disabledReasonInfo,
    [COMMUNITY_RUN_DISABLED_TONE.WARNING]: classes.disabledReasonWarning,
    [COMMUNITY_RUN_DISABLED_TONE.ERROR]: classes.disabledReasonError,
};

const ConfigurationRunDock = ({
    totalSimulations = 0,
    numToBeRun = 0,
    availableRuns = 0,
    insufficientRuns,
    runDisabled,
    runDisabledReason,
    isSimulationRunning,
    isPreparingRun,
    onUnlockRunsClick,
    communityId,
    measureStats,
    handleCommunityParametricRunClick,
    hasMissingCodes,
    ecmTableSyncIssues,
    validationComboOutOfRange,
    validationInProgress,
    validationStarting,
}) => {
    const [isRunModalOpen, setIsRunModalOpen] = useState(false);

    const checks = useMemo(
        () =>
            buildCommunityReadinessChecks({
                hasMissingCodes,
                ecmTableSyncIssues,
                measureStats,
                validationComboOutOfRange,
                insufficientRuns,
                validationInProgress: validationInProgress || validationStarting,
            }),
        [
            hasMissingCodes,
            ecmTableSyncIssues,
            measureStats,
            validationComboOutOfRange,
            insufficientRuns,
            validationInProgress,
            validationStarting,
        ]
    );

    const { passing, total, failing } = getReadinessSummary(checks);

    return (
        <>
            <div className={classes.runDock}>
                <div className={classes.summaryBlock}>
                    <p className={classes.readinessSummary}>
                        Readiness: <strong>{passing}</strong> of {total} checks passing
                        {failing > 0 ? (
                            <>
                                {" "}
                                ·{" "}
                                <a className={classes.readinessLink} href="#community-readiness-checklist">
                                    View checklist
                                </a>
                            </>
                        ) : null}
                    </p>
                </div>

                <div className={classes.runActionBlock}>
                    <div className={classes.runActionRow}>
                        <div className={classes.nextRunSummary}>
                            <p className={classes.nextRunLabel}>Simulations in the next run</p>
                            <p className={classes.nextRunValue}>
                                {Number(numToBeRun || 0).toLocaleString()}
                            </p>
                        </div>
                        <Button
                            className={classes.runButton}
                            disabled={runDisabled}
                            onClick={() => setIsRunModalOpen(true)}
                        >
                            {isSimulationRunning
                                ? "Running simulations…"
                                : isPreparingRun
                                  ? "Preparing run…"
                                  : "Run simulations"}
                        </Button>
                    </div>
                    {runDisabled && runDisabledReason ? (
                        <p
                            className={classNames(
                                classes.disabledReason,
                                RUN_DISABLED_TONE_CLASS[runDisabledReason.tone]
                            )}
                            role="status"
                        >
                            {runDisabledReason.message}
                        </p>
                    ) : null}
                </div>
            </div>

            <RunConfirmationModal
                open={isRunModalOpen}
                onClose={() => setIsRunModalOpen(false)}
                onProceed={handleCommunityParametricRunClick}
                communityId={communityId}
                availableRuns={availableRuns}
                totalSimulations={totalSimulations}
                numToBeRun={numToBeRun || 0}
                insufficientRuns={insufficientRuns}
                isPreparingRun={isPreparingRun}
                onUnlockRunsClick={() => {
                    setIsRunModalOpen(false);
                    onUnlockRunsClick?.();
                }}
            />
        </>
    );
};

export default ConfigurationRunDock;
